import * as React from 'react';
import { ApSimpleDropdown } from '@alixpartners/ui-components';
import { ScrollContainer } from 'components/ScrollContainer';
import FilterPanel from 'components/FilterPanel';
import { ApiContext } from 'context/ApiContext';
import {
  ICurrencyFormat,
  IDisplayByType,
  ISettingsResponse,
} from 'utils/types';
import NoData from 'views/NoData/NoData';
import {
  HeaderLabel,
  HeaderTop,
  NoDataContainer,
  SecondaryContent,
  SectionWrapper,
  Wrapper,
} from './Products.styles';
import ChartOptions from './ChartOptions/ChartOptions';
import QueryParams from 'context/QueryParams';
import { Chart } from './Chart/Chart';
import { ProductsTable } from 'components/Tables/ProductsTable/ProductsTable';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import moment from 'moment';
import {
  DISPLAY_BY,
  DISPLAY_BY_TO_SPEND_VIEW_TYPE,
  EXPORT_OPTIONS,
} from 'utils/constants';
import {
  exportProducts,
  exportProductsAppSpend,
  exportProductsAppAllocationSpend,
  getProductsSpend,
  getSpendByApplicationAndProduct,
  getSpendByProduct,
} from 'utils/api';
import {
  EMPTY_CHART_DATA,
  getQueryParams,
  getSeriesFromProducts,
  getSeriesFromSpendByApplication,
  getSeriesFromSpendByApplicationL2,
  getSeriesFromSpendByProduct,
} from './utils';
import {
  IApplicationSpend,
  IChartData,
  IChartOptionsConfig,
  IProductResponse,
  ISpendByProductResponse,
} from './types';

const FILTERS_LOCAL_STORAGE_KEY = 'TXR_PRODUCTS_ANALYSIS_FILTERS_KEY';
const LOCAL_STORAGE_KEY = 'TXR_PRODUCTS_ANALYSIS_CONFIG_KEY';

const defaultStateConfig: IChartOptionsConfig = {
  contributions: undefined,
  displayBy: IDisplayByType.COST,
  level1: undefined,
  level2: undefined,
  level3: undefined,
  revenueCheckbox: true,
  costCheckbox: true,
  costAsPercentageCheckbox: true,
  selectedCategory: undefined,
};

export const ProductsView = ({
  format,
  params,
  permissions,
  settings,
}: {
  format: ICurrencyFormat;
  params: { [key: string]: string | string[] };
  permissions: string[];
  settings: ISettingsResponse;
}) => {
  const chartRef = React.useRef<any>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [products, setProducts] = React.useState<IProductResponse>();
  const [spendByProduct, setSpendByProduct] =
    React.useState<ISpendByProductResponse>();
  const [spendByApplication, setSpendByApplication] =
    React.useState<IApplicationSpend[]>();
  const [chartOptionsConfig, setChartOptionsConfig] =
    React.useState<IChartOptionsConfig>(defaultStateConfig);
  const [chartData, setChartData] =
    React.useState<IChartData>(EMPTY_CHART_DATA);
  const {
    contributions,
    displayBy,
    level1,
    level2,
    level3,
    costAsPercentageCheckbox,
    selectedCategory,
  } = chartOptionsConfig;
  const isHeadcount = displayBy === DISPLAY_BY.HEADCOUNT;
  const getProducts = () => {
    if (settings) {
      getProductsSpend(getQueryParams(params, settings)).then(
        (resp: IProductResponse) => {
          setProducts(resp);
          setIsLoading(false);
        }
      );
    }
  };

  const getApplicationsByProduct = () => {
    if (level1 && products) {
      const foundProduct = products?.productsSpend.find(
        (product) => product.productName === level1
      );
      if (foundProduct) {
        getSpendByProduct({
          query: getQueryParams(params, settings),
          productId: foundProduct.productId,
        }).then((resp: ISpendByProductResponse) => {
          setSpendByProduct(resp);
        });
      }
    }
  };

  const getSpendByApplication = () => {
    if (level1 && level2 && products && spendByProduct) {
      const foundProduct = products?.productsSpend.find(
        (product) => product.productName === level1
      );
      const foundApplication = spendByProduct?.applicationsSpendByProduct.find(
        (application) => application.applicationName === level2
      );
      if (foundProduct && foundApplication) {
        getSpendByApplicationAndProduct({
          query: getQueryParams(params, settings),
          applicationId: foundApplication.applicationId,
          productId: foundProduct.productId,
        }).then((resp: IApplicationSpend[]) => {
          setSpendByApplication(resp);
        });
      }
    }
  };

  React.useEffect(() => {
    getApplicationsByProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level1, params, products]);

  React.useEffect(() => {
    getSpendByApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level1, level2, params, products, spendByProduct]);

  React.useEffect(() => {
    const localStorageState = getLocalStorageItem(LOCAL_STORAGE_KEY);
    if (localStorageState) {
      setChartOptionsConfig(localStorageState);
    }
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, params]);

  const updateChartOptionsConfig = (config: IChartOptionsConfig) => {
    setLocalStorageItem(LOCAL_STORAGE_KEY, config);
    setChartOptionsConfig(config);
  };
  React.useEffect(() => {
    if (level3) {
      setChartData(
        getSeriesFromSpendByApplicationL2({
          spendByApplication,
          chartOptionsConfig,
          format,
        })
      );
    } else if (level2) {
      setChartData(
        getSeriesFromSpendByApplication({
          spendByApplication,
          chartOptionsConfig,
        })
      );
    } else if (level1) {
      setChartData(
        getSeriesFromSpendByProduct({
          spendByProduct,
          chartOptionsConfig,
        })
      );
    } else {
      setChartData(
        getSeriesFromProducts({
          productsResponse: products,
          chartOptionsConfig,
        })
      );
    }
  }, [
    products,
    spendByProduct,
    spendByApplication,
    level3,
    level2,
    level1,
    format,
    params,
    contributions,
    chartOptionsConfig,
  ]);

  const isSettingsAvailable =
    settings &&
    settings.availableRange &&
    settings.availableRange.startYear &&
    settings.availableRange.endYear;
  if (!chartData || isLoading) {
    return null;
  }
  const { categories, legend, series, tableData } = chartData;

  const getChartInstance = () => {
    return chartRef && chartRef.current && chartRef.current.chart;
  };

  const handleExport = (chartInstance: any, filename: string) => {
    if (chartInstance) {
      chartInstance.exportChart({ filename }, { chart: { width: 1200 } });
    }
  };

  const exportData = (isTableExport?: boolean) => {
    const foundProduct = products?.productsSpend.find(
      (product) => product.productName === level1
    );
    const foundApplication = spendByProduct?.applicationsSpendByProduct.find(
      (application) => application.applicationName === level2
    );
    let contributionsQuery = '';
    let tableParam = '';
    if (contributions) {
      contributionsQuery = `&contributions=${contributions}`;
    }
    if (isTableExport) {
      tableParam = `&isTableExport=true`;
    }
    let query = `${getQueryParams(
      params,
      settings
    )}${contributionsQuery}${tableParam}`;
    if (level3) {
      exportProductsAppAllocationSpend(query, {
        spendViewType: DISPLAY_BY_TO_SPEND_VIEW_TYPE[displayBy],
        productId: foundProduct?.productId || 0,
        applicationId: foundApplication?.applicationId || 0,
        l1ServiceTowerName: level3,
      });
    } else if (level2) {
      exportProductsAppAllocationSpend(query, {
        spendViewType: DISPLAY_BY_TO_SPEND_VIEW_TYPE[displayBy],
        productId: foundProduct?.productId || 0,
        applicationId: foundApplication?.applicationId || 0,
      });
    } else if (level1) {
      exportProductsAppSpend(query, {
        spendViewType: DISPLAY_BY_TO_SPEND_VIEW_TYPE[displayBy],
        productId: foundProduct?.productId || 0,
      });
    } else {
      exportProducts(query, {
        spendViewType: DISPLAY_BY_TO_SPEND_VIEW_TYPE[displayBy],
      });
    }
  };

  const exportChart = (val: any) => {
    if (val.value === 'png') {
      handleExport(
        getChartInstance(),
        `TXR_product_summary-${moment().format()}`
      );
    } else if (val.value === 'xlsx') {
      exportData();
    }
  };

  const tableTitleDisplayBy = isHeadcount ? 'headcount' : 'spend';
  let tableTitle = `Summary ${tableTitleDisplayBy} by product`;
  if (level3) {
    tableTitle = `${level1} ${tableTitleDisplayBy} by ${level2}, ${level3}`;
  } else if (level2) {
    tableTitle = `${level1} ${tableTitleDisplayBy} by ${level2}`;
  } else if (level1) {
    tableTitle = `${level1} ${tableTitleDisplayBy} by applications`;
  }
  const isNoData = categories.length === 0 && !isLoading;
  return isSettingsAvailable && chartOptionsConfig ? (
    <>
      <FilterPanel
        localStorageKey={FILTERS_LOCAL_STORAGE_KEY}
        disabledFilters={['Geography']}
      />
      <ScrollContainer>
        <Wrapper>
          <ChartOptions
            chartOptionsConfig={chartOptionsConfig}
            setChartOptionsConfig={updateChartOptionsConfig}
            onExport={exportChart}
            permissions={permissions}
          />
          {isNoData ? (
            <NoDataContainer>
              <h4>No Data Available for this combination of filters</h4>
              <br />
              <h6>Please try to reset or change the filter options</h6>
            </NoDataContainer>
          ) : (
            <Chart
              chartRef={chartRef}
              categories={categories}
              displayType={displayBy}
              isPercentageAxis={costAsPercentageCheckbox && !level1}
              legend={legend}
              format={format}
              selectedCategory={selectedCategory}
              series={series}
              onClick={(category, isSelectingCategory) => {
                if (isSelectingCategory) {
                  updateChartOptionsConfig({
                    ...chartOptionsConfig,
                    selectedCategory:
                      category !== selectedCategory ? category : undefined,
                  });
                  return;
                }
                if (level3) {
                  return;
                } else if (level2) {
                  updateChartOptionsConfig({
                    ...chartOptionsConfig,
                    level3: category,
                    selectedCategory: undefined,
                  });
                } else if (level1) {
                  updateChartOptionsConfig({
                    ...chartOptionsConfig,
                    level2: category,
                    selectedCategory: undefined,
                  });
                } else {
                  updateChartOptionsConfig({
                    ...chartOptionsConfig,
                    level1: category,
                    selectedCategory: undefined,
                  });
                }
              }}
            />
          )}
        </Wrapper>
        <SecondaryContent>
          <SectionWrapper>
            <HeaderTop>
              <HeaderLabel>{tableTitle}</HeaderLabel>
              <ApSimpleDropdown
                iconName="outline_save_alt"
                onChange={(s: any) => {
                  if (s.value === 'xlsx') {
                    exportData(true);
                  }
                }}
                options={[EXPORT_OPTIONS[1]]}
                label="Export"
              />
            </HeaderTop>
            <ProductsTable
              chartOptionsConfig={chartOptionsConfig}
              rowData={tableData.map((application) => ({
                ...application,
                isHighlighted: application.name === selectedCategory,
              }))}
              isHeadcount={isHeadcount}
              format={format}
            />
          </SectionWrapper>
        </SecondaryContent>
      </ScrollContainer>
    </>
  ) : (
    <NoData
      buttonText="upload product data"
      text="Product allocation is not defined"
      link="/settings/data/products"
      description="Please upload a list of products and allocate spend"
    />
  );
};

export const Products = (props: any) => (
  <QueryParams>
    {({ params }) => (
      <ApiContext.Consumer>
        {({ format, permissions, settings }) => (
          <ProductsView
            {...props}
            format={format}
            params={params}
            permissions={permissions}
            settings={settings}
          />
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);
